// Copyright (c) 2026 PGNS LLC

/**
 * Presentation logic for status data.
 *
 * Everything here is pure so it can be unit-tested without a DOM: what a status
 * page claims about uptime is the whole product, and a rounding bug that turns
 * 99.4% into "100%" is a lie about an outage.
 */

import type { ComponentStatus, HistoryComponent, HistoryDay, Incident } from './api';

export const STATUS_LABEL: Record<ComponentStatus, string> = {
  up: 'Operational',
  degraded: 'Degraded',
  down: 'Outage',
  unknown: 'Unknown',
};

/**
 * Tailwind background classes per status, on OKLCH semantic tokens so light and
 * dark modes both work without a second palette.
 */
export const STATUS_FILL: Record<ComponentStatus, string> = {
  up: 'bg-brand-green',
  degraded: 'bg-brand-bronze',
  down: 'bg-destructive',
  unknown: 'bg-muted-foreground/40',
};

export const STATUS_TEXT: Record<ComponentStatus, string> = {
  up: 'text-brand-green',
  degraded: 'text-brand-bronze',
  down: 'text-destructive',
  unknown: 'text-muted-foreground',
};

const IMPACT_LABEL: Record<string, string> = {
  degraded: 'Degraded performance',
  partial: 'Partial outage',
  major: 'Major outage',
  maintenance: 'Maintenance',
};

export function impactLabel(impact: string): string {
  return IMPACT_LABEL[impact] ?? impact;
}

export function impactFill(impact: string): string {
  if (impact === 'major') return STATUS_FILL.down;
  if (impact === 'maintenance') return 'bg-brand-blue';
  return STATUS_FILL.degraded;
}

/** Human label for a component group, keyed by the Worker's `kind` column. */
const KIND_LABEL: Record<string, string> = {
  probe: 'Endpoints',
  slo: 'Service level',
};

export function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind;
}

/**
 * Group components into sections in the order the kinds first appear.
 *
 * The Worker already sorts by `position`, so preserving encounter order keeps
 * the page's section order under the Worker's control rather than this file's.
 */
export function groupByKind<T extends { kind: string }>(items: T[]): Array<[string, T[]]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const existing = groups.get(item.kind);
    if (existing) existing.push(item);
    else groups.set(item.kind, [item]);
  }
  return Array.from(groups.entries());
}

/**
 * Format an availability ratio as a percentage.
 *
 * Truncates rather than rounds, and never prints "100%" for anything short of a
 * perfect window: a page that rounds 99.996% up to 100% erases the incident it
 * exists to report.
 */
export function formatAvailability(ratio: number | null): string {
  if (ratio === null) return '—';
  if (ratio >= 1) return '100%';
  const truncated = Math.floor(ratio * 100_000) / 1000;
  return `${truncated.toFixed(3)}%`;
}

export function formatMs(ms: number | null): string {
  if (ms === null) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${Math.round(ms)} ms`;
}

/** `2026-07-27` -> `Jul 27` — the strip is dense, so no year and no weekday. */
export function formatDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Coarse relative age — a status page needs "3 minutes ago", not "3:41 ago". */
export function formatRelative(iso: string | null, now: number): string {
  if (iso === null) return 'never';
  const deltaSeconds = Math.round((now - new Date(iso).getTime()) / 1000);
  if (deltaSeconds < 0) return 'just now';
  if (deltaSeconds < 60) return `${deltaSeconds}s ago`;
  if (deltaSeconds < 3600) return `${Math.floor(deltaSeconds / 60)}m ago`;
  if (deltaSeconds < 86_400) return `${Math.floor(deltaSeconds / 3600)}h ago`;
  return `${Math.floor(deltaSeconds / 86_400)}d ago`;
}

export function incidentDuration(incident: Incident): string {
  const end = incident.resolved_at === null ? Date.now() : new Date(incident.resolved_at).getTime();
  const minutes = Math.max(0, Math.round((end - new Date(incident.started_at).getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h ${minutes % 60}m` : `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/** Uptime across a component's whole history window, from measured seconds. */
export function windowAvailability(days: HistoryDay[]): number | null {
  let up = 0;
  let degraded = 0;
  let down = 0;
  for (const day of days) {
    up += day.up_seconds;
    degraded += day.degraded_seconds;
    down += day.down_seconds;
  }
  const measured = up + degraded + down;
  return measured === 0 ? null : (up + degraded) / measured;
}

export interface LatencySample {
  day: string;
  p50: number | null;
  p95: number | null;
}

export interface LatencySeries {
  samples: LatencySample[];
  latestP50: number | null;
  latestP95: number | null;
  /** Polyline points in a 0..1 unit box, oldest first; empty when unplottable. */
  points: Array<{ x: number; y: number }>;
  max: number;
}

/**
 * Build a p95 sparkline over a component's history.
 *
 * Days with no samples carry `null` percentiles and are dropped rather than
 * plotted as zero — a gap in measurement is not a fast day. A single point
 * cannot form a line, so it yields no points and the widget renders numbers
 * only.
 */
export function latencySeries(days: HistoryDay[]): LatencySeries {
  const samples: LatencySample[] = days.map((day) => ({
    day: day.day,
    p50: day.p50_ms,
    p95: day.p95_ms,
  }));
  const plottable = samples.filter(
    (sample): sample is LatencySample & { p95: number } => sample.p95 !== null,
  );
  const latest = plottable.at(-1);
  const max = plottable.reduce((worst, sample) => Math.max(worst, sample.p95), 0);
  const points =
    plottable.length < 2 || max === 0
      ? []
      : plottable.map((sample, index) => ({
          x: index / (plottable.length - 1),
          // SVG y grows downward, so invert: a high p95 must sit high on screen.
          y: 1 - sample.p95 / max,
        }));
  return {
    samples,
    latestP50: latest?.p50 ?? null,
    latestP95: latest?.p95 ?? null,
    points,
    max,
  };
}

/** Turn unit-box points into an SVG `points` attribute for a viewBox of w x h. */
export function polylinePoints(
  points: Array<{ x: number; y: number }>,
  width: number,
  height: number,
): string {
  return points
    .map((point) => `${(point.x * width).toFixed(2)},${(point.y * height).toFixed(2)}`)
    .join(' ');
}

/**
 * Index history by component id so a summary component can find its strip.
 *
 * Summary and history are separate requests that can disagree — a component
 * added between them exists in one and not the other — so callers must handle a
 * miss instead of assuming alignment.
 */
export function historyById(components: HistoryComponent[]): Map<string, HistoryComponent> {
  return new Map(components.map((component) => [component.id, component]));
}
